import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Music2, Disc3, FolderKanban } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export function NewItemDialog({ open, onOpenChange }) {
  const [step, setStep] = useState("choose");
  const [kind, setKind] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("single");
  const [projectId, setProjectId] = useState("");
  const [projectType, setProjectType] = useState("ep");
  const [bpm, setBpm] = useState("");
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setStep("choose"); setKind(null); setName(""); setType("single"); setProjectId(""); setBpm(""); setProjectType("ep");
      api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    }
  }, [open]);

  const pick = (k) => { setKind(k); setStep("form"); };

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (kind === "song") {
        const body = { name: name.trim(), type, bpm: bpm ? parseInt(bpm) : null };
        if (type === "project" && projectId) body.project_id = projectId;
        const { data } = await api.post("/songs", body);
        toast.success("Song created");
        onOpenChange(false);
        navigate(`/songs/${data.id}`);
      } else if (kind === "beat") {
        const { data } = await api.post("/beats", { name: name.trim(), bpm: bpm ? parseInt(bpm) : null });
        toast.success("Beat created");
        onOpenChange(false);
        navigate(`/beats?highlight=${data.id}`);
      } else if (kind === "project") {
        const { data } = await api.post("/projects", { name: name.trim(), type: projectType });
        toast.success("Project created");
        onOpenChange(false);
        navigate(`/projects/${data.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141416] border-white/10 text-white" data-testid="new-item-dialog">
        <DialogHeader>
          <DialogTitle className="tracking-tight">
            {step === "choose" ? "What do you want to create?" : `New ${kind}`}
          </DialogTitle>
        </DialogHeader>
        {step === "choose" && (
          <div className="grid grid-cols-3 gap-3 pt-2">
            {[
              { k: "song", label: "Song", Icon: Music2 },
              { k: "beat", label: "Beat", Icon: Disc3 },
              { k: "project", label: "Project", Icon: FolderKanban },
            ].map(({ k, label, Icon }) => (
              <button key={k} onClick={() => pick(k)} data-testid={`new-choose-${k}`}
                className="flex flex-col items-center gap-2 p-6 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-violet-500/50 transition-colors">
                <Icon className="w-7 h-7 text-violet-400" />
                <span className="text-sm">{label}</span>
              </button>
            ))}
          </div>
        )}
        {step === "form" && (
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                data-testid="new-name-input"
                placeholder={kind === "beat" ? "e.g. Beat 001" : "Type a name"}
                className="bg-black/40 border-white/10 mt-1" />
            </div>
            {kind === "song" && (
              <div>
                <Label className="text-xs text-zinc-400">Type</Label>
                <RadioGroup value={type} onValueChange={setType} className="grid grid-cols-2 gap-2 mt-1">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${type === "single" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                    <RadioGroupItem value="single" data-testid="new-type-single" /> <span className="text-sm">Single</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${type === "project" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                    <RadioGroupItem value="project" data-testid="new-type-project" /> <span className="text-sm">In Project</span>
                  </label>
                </RadioGroup>
                {type === "project" && projects.length > 0 && (
                  <div className="mt-3">
                    <Label className="text-xs text-zinc-400">Project</Label>
                    <Select value={projectId} onValueChange={setProjectId}>
                      <SelectTrigger className="bg-black/40 border-white/10 mt-1" data-testid="new-project-select">
                        <SelectValue placeholder="Choose project" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141416] border-white/10 text-white">
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {type === "project" && projects.length === 0 && (
                  <p className="text-xs text-zinc-500 mt-2">No projects yet. Create one first.</p>
                )}
              </div>
            )}
            {(kind === "song" || kind === "beat") && (
              <div>
                <Label className="text-xs text-zinc-400">BPM (optional)</Label>
                <Input value={bpm} onChange={(e) => setBpm(e.target.value.replace(/\D/g, ""))} inputMode="numeric"
                  className="bg-black/40 border-white/10 mt-1" placeholder="e.g. 90" data-testid="new-bpm-input" />
              </div>
            )}
            {kind === "project" && (
              <div>
                <Label className="text-xs text-zinc-400">Type</Label>
                <RadioGroup value={projectType} onValueChange={setProjectType} className="grid grid-cols-2 gap-2 mt-1">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${projectType === "ep" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                    <RadioGroupItem value="ep" data-testid="new-project-ep" /> <span className="text-sm">EP</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${projectType === "album" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                    <RadioGroupItem value="album" data-testid="new-project-album" /> <span className="text-sm">Album</span>
                  </label>
                </RadioGroup>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep("choose")} className="text-zinc-400">Back</Button>
              <Button onClick={submit} disabled={saving} className="ml-auto bg-violet-600 hover:bg-violet-500 rounded-full" data-testid="new-submit-btn">
                {saving ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
